import { CSSResultGroup, html, LitElement, nothing, TemplateResult } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { FILTER_WIZARD_STORE, FilterWizardStore } from 'components/discovery/filter-wizard/filter-wizard-store.js'
import { FilterStepOverview, FilterStepPagination } from 'components/discovery/filter-wizard/typing/internal.js'
import { FilterNavigationDisplayOptions } from 'components/discovery/filter-wizard/typing/config.js'
import { style } from './filter-navigation.style.js'
import { FilterAttributes } from 'components/discovery/shared/search/typing/filter-attributes.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'

@customElement('csc-filter-navigation')
export class CSCFilterNavigation extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Object, attribute: 'display-options' })
  displayOptions: FilterNavigationDisplayOptions | null = null

  @inject(FILTER_WIZARD_STORE)
  private filterWizardStore!: FilterWizardStore

  @state()
  private filterStepOverview!: FilterStepOverview[]

  @state()
  private filterAttributes!: FilterAttributes

  @state()
  private filterStepPagination!: FilterStepPagination

  constructor() {
    super()

    createEffect(() => {
      this.filterStepOverview = this.filterWizardStore.filterStepOverview()
      this.filterAttributes = this.filterWizardStore.attributes()
      this.filterStepPagination = this.filterWizardStore.filterStepPagination()
    })
  }

  private renderFilterSelection = (attributeId: number, valueId: number): string => {
    return this.filterAttributes.getAttributeValue(attributeId, valueId).name
  }

  private hasBeenSkipped = (hasSelection: boolean, stepIndex: number) => {
    return stepIndex < this.filterStepPagination.activeStep && !hasSelection
  }

  private getState = (hasSelection: boolean, stepIndex: number): 'skipped' | 'completed' | 'none' => {
    if (this.hasBeenSkipped(hasSelection, stepIndex)) {
      return 'skipped'
    }

    if (hasSelection) {
      return 'completed'
    }

    return 'none'
  }

  private handleStepClick = (event: Event, index: number): void => {
    event.stopPropagation()
    this.filterWizardStore.gotoStep(index)
  }

  protected override render(): TemplateResult {
    const paperConfig = this.displayOptions?.paper

    return html`
      <cse-paper
        class="csc-filter-navigation"
        variant="${ifDefined(paperConfig?.variant)}"
        size="${ifDefined(paperConfig?.size)}"
        ?bordered="${paperConfig?.bordered}"
      >
        <cse-selectable-group
          class="step-overview-group"
          selected-indices=${JSON.stringify([this.filterStepPagination.activeStep])}
        >
          ${this.filterStepOverview.map((filterStep, stepIndex) => {
            return html`
              <cse-selectable
                class="step-overview ${this.getState(filterStep.hasSelection, stepIndex)}"
                id="step-${filterStep.attributeId}-overview"
                @click=${(e: Event) => this.handleStepClick(e, stepIndex)}
              >
                <div class="step-text">
                  <cse-text class="step-name">${filterStep.name}</cse-text>
                  ${!this.displayOptions?.showNavIcons || !filterStep.hasSelection ? nothing : html`
                    <cse-icon icon-name="check" icon-style="regular"></cse-icon>
                  ` }
                  ${!this.displayOptions?.showNavIcons || !this.hasBeenSkipped(filterStep.hasSelection, stepIndex) ? nothing : html`
                    <cse-icon icon-name="minus" icon-style="regular"></cse-icon>
                  ` }
                </div>
                ${filterStep.selectedValueIds.length ? html`
                  <cse-text class="step-selection" variant="caption-l" id="step-${filterStep.attributeId}-selection">
                    ${filterStep.selectedValueIds.map(valueId => this.renderFilterSelection(filterStep.attributeId, valueId)).join(', ')}
                  </cse-text>
                ` : nothing}
              </cse-selectable>
            `
          })}
        </cse-selectable-group>

        <cse-link role="button" class="clear-selection" @click="${() => this.filterWizardStore.clearSelection()}">
          ${t('clear_selection_button', { ns: 'csc-filter-navigation', default: 'Clear selection' })}
        </cse-link>
      </cse-paper>
    `
  }
}
